import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#collapseTimer);
    clearTimeout(this.#openHeightTimer);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupResizeObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Grace-period timer before a genuine leave actually closes the active submenu.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #closeTimer;

  /**
   * How long to wait, in ms, after a genuine leave before closing the submenu — gives the
   * pointer/focus a short window to come back (e.g. re-entering via a gap between elements)
   * without the panel flickering shut.
   */
  static #CLOSE_DELAY = 200;

  /**
   * Timer that collapses the panel's height/clip-path once its CSS opacity fade-out has had
   * time to play.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #collapseTimer;

  /**
   * How long, in ms, the CSS opacity fade-out actually takes — must stay in sync with
   * `--submenu-content-animation` / the visibility transition-delay in _header-menu.liquid
   * (`--submenu-animation-speed` * 0.75, i.e. 360ms * 0.75).
   */
  static #SUBMENU_FADE_DURATION = 270;

  /**
   * Timer that applies a newly-activated item's height/clip once the previous item's fade-out
   * has had time to play, so switching between nav items shows a visible close animation
   * instead of the new height snapping in immediately and clipping the old panel away.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #openHeightTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (!listItem || listItem.contains(this.#state.activeItem)) return;

      // Waterloo: guard against dispatching onto an item whose submenu is already marked
      // active — re-firing pointerenter on it would restart its open transition and can
      // read as a flicker even though nothing actually changed.
      const submenu = findSubmenu(findMenuItem(listItem));
      if (submenu?.dataset.active !== undefined) return;

      listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
    });
  }

  /**
   * Whether the pointer is still travelling toward the currently open submenu — a diagonal path
   * that happens to cross another trigger on the way — rather than genuinely heading for the
   * newly-entered item.
   * @param {PointerEvent} event
   * @returns {boolean}
   */
  #isHeadingIntoOpenSubmenu(event) {
    const openSubmenu = findSubmenu(this.#state.activeItem);
    if (!openSubmenu) return false;

    const rect = openSubmenu.getBoundingClientRect();
    if (rect.height === 0) return false;

    // Once the pointer has actually reached the panel, .menu-list__submenu:hover (CSS) and
    // #reconcilePointerTarget (JS) already keep things in sync — this guard only matters
    // while still above it, in the header row where triggers sit.
    if (event.clientY >= rect.top) return false;

    const dy = event.clientY - this.#lastPointer.y;
    const dx = event.clientX - this.#lastPointer.x;

    // Require a clearly-downward component — a same-row hop to a sibling trigger has a dy near
    // zero and should switch immediately, not get caught by this guard.
    if (dy <= 2) return false;

    // Waterloo: also require the movement to be *predominantly* downward, not just non-zero.
    // A same-row hop between adjacent triggers (e.g. Learn -> Our Story) is mostly horizontal
    // but, being real pointer input rather than a mathematically flat line, almost always carries
    // a few px of incidental vertical noise — enough to clear the `dy <= 2` check above on its
    // own. Since a full-width mega menu's rect spans the entire row, the projectedX check below
    // is satisfied by virtually any point, so that noise alone was enough to misclassify an
    // ordinary sideways hop as "heading into the panel" and swallow the new trigger's activation,
    // leaving it to a slower fallback (or the close timer) to sort out — read as the panel
    // flickering or "going away" instead of the hop just working immediately.
    if (dy <= Math.abs(dx) * 0.3) return false;

    // Project the current trajectory forward to where it would cross the submenu's top edge,
    // and check whether that lands within (or near) its horizontal bounds.
    const projectedX = event.clientX + dx * ((rect.top - event.clientY) / dy);
    return projectedX >= rect.left - 40 && projectedX <= rect.right + 40;
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    // Waterloo: bail out before touching any timers if this event doesn't actually resolve to a
    // menu item — e.g. a pointerenter/focus that bubbled up from something other than a trigger's
    // own link as a submenu fades out and its contents shift focus/hit-testing around. Clearing
    // #collapseTimer for a spurious event like this (as used to happen here, unconditionally,
    // before this check) cancels the pending reset of --full-open-header-height with nothing left
    // to reschedule it, leaving the navy underlay stuck open at its last height indefinitely —
    // intermittently, since it depends on exactly when the spurious event lands.
    if (!item) return;

    // Cancel any pending close from a previous leave — the pointer/focus is back before the
    // grace period in `deactivate()` ran out. This is safe to do even for a redundant activation
    // of the item that's already active (handled by the early return right below): it only cancels
    // *this* item's own pending close.
    clearTimeout(this.#closeTimer);

    if (item == this.#state.activeItem) return;

    // Waterloo: #collapseTimer and #openHeightTimer are only relevant to an *actual* switch away
    // from a previous item — #collapseTimer finishes deleting the previous submenu's data-active
    // flag, #openHeightTimer applies the new item's height once the old one's fade has played.
    // Clearing them has to wait until here (after the same-item early return above): a redundant
    // activate() call for the item that's already active — e.g. a focus event landing alongside
    // pointerenter, or the pointer crossing back and forth over the hit-area extension in
    // .menu-list__list-item::after near a item boundary — used to clear these unconditionally at
    // the top of this function. That cancelled a still-pending, unrelated cleanup left over from
    // the *previous* switch without ever rescheduling it, permanently orphaning the old submenu's
    // data-active flag and leaving the navy underlay stuck open at its last height.
    clearTimeout(this.#collapseTimer);
    clearTimeout(this.#openHeightTimer);

    // Waterloo: "safe triangle" guard. A diagonal path from one trigger word down into its
    // already-open mega-menu content (e.g. toward a link that sits further left/right than the
    // trigger itself) necessarily crosses the header row, where it can pass over a *different*
    // trigger's hit area along the way. Without this, that trigger's genuine pointerenter would
    // steal activation and close the panel the user was actually heading for. If the pointer is
    // still travelling downward on a path that lands inside the currently open submenu, ignore
    // this activation — #onPointerMove's idle-settle check (#reconcilePointerTarget) will
    // correctly activate `item` instead once the pointer actually stops moving over it.
    if (
      event instanceof PointerEvent &&
      event.pointerType !== 'touch' &&
      this.#state.activeItem &&
      this.#isHeadingIntoOpenSubmenu(event)
    ) {
      return;
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      // Waterloo: this used to only run inside the `if (submenu)` branch below, which only
      // executes when the *new* item also has its own submenu. Switching to an item with no
      // submenu of its own (e.g. a plain top-level link) skipped it entirely, leaving the old
      // item's ResizeObserver watching its now-hidden submenu — if it fired after the switch, it
      // would re-inflate --full-open-header-height back to the old submenu's height, showing a
      // tall blank navy background with no visible content underneath it.
      this.#cleanupResizeObserver();

      // Waterloo: switching directly from one item to another (no intervening
      // #deactivate()) previously left the old submenu's dataset.active flag set forever,
      // since only #deactivate() clears it. That stale flag both defeats the
      // content-visibility optimization and could make a since-closed submenu look "active"
      // to the pointer-target reconciliation, letting it flash back in.
      //
      // Clear it on the same delay as the CSS fade-out (see _header-menu.liquid) instead of
      // instantly, so the fade actually gets to play for a direct switch too — the earlier fix
      // for the stale-background bug forced an instant hide here, which killed the fade outright.
      // The real cause of that bug was the stale ResizeObserver above, now cleaned up on every
      // switch regardless of what's activating next, so this no longer needs to be instant.
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        clearTimeout(this.#collapseTimer);
        this.#collapseTimer = setTimeout(() => {
          delete previousSubmenu.dataset.active;
        }, HeaderMenu.#SUBMENU_FADE_DURATION);
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Waterloo: undo the immediate pointer-events:none applied at the start of
      // #deactivate() (see below) in case this submenu is being reopened before its
      // close transition finished.
      submenu.style.removeProperty('pointer-events');

      // Cleanup any existing resize observer from previous menu activations
      this.#cleanupResizeObserver();

      // Waterloo: watches for the submenu's rendered size changing after this initial
      // measurement — e.g. a mega menu's carousel images still loading on a fast hover
      // right after page reload, before they have an intrinsic size. A MutationObserver
      // (the previous approach) only catches DOM nodes being added/removed, not existing
      // <img> elements finishing their load and changing the layout, so a too-short height
      // measured before images loaded could never get corrected — the submenu panel would
      // stay clipped mid-content, with the page's own content showing through underneath.
      // ResizeObserver catches a height change regardless of what caused it.
      this.#submenuResizeObserver = new ResizeObserver(() => {
        if (submenu.offsetHeight > 0) {
          this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
          this.#setFullOpenHeaderHeight(submenu.offsetHeight);
        }
      });
      this.#submenuResizeObserver.observe(submenu);

      // Auto-disconnect after 2s — long enough to cover slow-loading images, short enough
      // to not keep observing indefinitely.
      setTimeout(() => {
        this.#cleanupResizeObserver();
      }, 2000);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const applyOpenHeight = () => {
      this.headerComponent?.style.setProperty('--submenu-height', `${finalHeight}px`);
      this.#setFullOpenHeaderHeight(finalHeight);
      this.style.setProperty('--submenu-opacity', '1');
    };

    if (previouslyActiveItem) {
      // Waterloo: wait for the previous item's fade-out to finish before applying this item's
      // height/clip. Applying it immediately (in the same tick the old item starts fading) was
      // clipping the old panel away instantly, leaving nothing visible to actually fade — the
      // close animation was invisible on every switch between nav items. Trade-off: this item's
      // own panel is still clipped to the *old* item's height until the delay elapses, so if
      // it's taller than the old one, it can appear briefly clipped before snapping open.
      clearTimeout(this.#openHeightTimer);
      this.#openHeightTimer = setTimeout(applyOpenHeight, HeaderMenu.#SUBMENU_FADE_DURATION);
    } else {
      applyOpenHeight();
    }

    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');
    // Waterloo: a genuine pointerleave/blur can never report a descendant of its own target as
    // relatedTarget (enter/leave events don't fire for moving within the same element's
    // subtree). When it happens anyway it's a spurious event that fires immediately after
    // activate() opens a mega menu, momentarily stealing hover from content inside the just-
    // opened submenu and closing it again with nothing new opening in its place.
    const isSpuriousSelfLeave = event.relatedTarget instanceof Node && event.target.contains(event.relatedTarget);

    if (isSpuriousSelfLeave) return;

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // Wait briefly before actually closing — gives the pointer/focus a short window to come
    // back (e.g. re-entering through a gap) without the panel flickering shut. `activate()`
    // cancels this timer if it fires again first; `#deactivate` itself re-checks that the item
    // is still the active one, in case something else already changed it by the time this runs.
    const item = this.#state.activeItem;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#deactivate(item);
    }, HeaderMenu.#CLOSE_DELAY);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    // A pending open-height application (from switching to this item) would otherwise apply
    // after this item has already been deactivated.
    clearTimeout(this.#openHeightTimer);

    const submenu = findSubmenu(item);

    // Waterloo: block pointer hits on the submenu the instant it starts closing, instead of
    // waiting for its CSS opacity/height transition to finish. While the transition is still
    // running the panel remains hit-testable, so a hover landing on it mid-close (or the
    // Safari pointer-target reconciliation re-discovering it via elementFromPoint) could
    // restart activation and produce the show-then-hide flicker. activate() clears this again
    // if the same submenu is reopened before the transition completes.
    submenu?.style.setProperty('pointer-events', 'none');

    // Stop watching the just-closed submenu — otherwise a stray resize during its close
    // transition (or a late-loading image elsewhere) can fire the observer after this point
    // and re-inflate --full-open-header-height, leaving the underlay's shadow visible even
    // though the menu is closed.
    this.#cleanupResizeObserver();

    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    // Waterloo: flipping aria-expanded is what triggers the CSS opacity fade-out (see
    // _header-menu.liquid's --submenu-content-opacity rules) and, on `.menu-list__submenu`, a
    // *delayed* switch to visibility:hidden — timed to let that fade actually play out.
    item.ariaExpanded = 'false';

    // Waterloo: collapsing the panel's height/clip-path has to wait for that same fade duration.
    // --full-open-header-height drives .menu-list__submenu's clip-path, so zeroing it in the same
    // tick as aria-expanded would clip the panel to zero-height immediately — hiding it before
    // the opacity transition has any visible effect, even though visibility briefly stays
    // 'visible'. Keeping content-visibility's `data-active` flag set for the same window stops
    // the browser from skipping the panel's rendering mid-fade.
    clearTimeout(this.#collapseTimer);
    this.#collapseTimer = setTimeout(() => {
      this.headerComponent?.style.setProperty('--submenu-height', '0px');
      this.#setFullOpenHeaderHeight(0);
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, HeaderMenu.#SUBMENU_FADE_DURATION);
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
